import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ShoppingBag,
  DollarSign,
  Users,
  Package,
  Store as StoreIcon,
  Sparkles,
  Loader2,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const PALETTE = ['#2563eb', '#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
const PERIODS = [
  { label: '7 dias', value: 7 },
  { label: '30 dias', value: 30 },
  { label: '90 dias', value: 90 },
];

const fmtBRL = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : v;

const fmtShort = (v) => {
  if (typeof v !== 'number') return v;
  if (v >= 1000000) return `R$ ${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(1)}k`;
  return `R$ ${v.toFixed(0)}`;
};

const fmtDay = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
};

export const Dashboard = () => {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dashboardAPI
      .premium(days)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const timeseries = useMemo(
    () => (data?.timeseries || []).map((d) => ({ ...d, dayLabel: fmtDay(d.date) })),
    [data]
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Dashboard Premium</p>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1">
            Olá, {user?.name || 'Administrador'} 👋
          </h1>
          <p className="text-slate-500 mt-1">Aqui está o desempenho do marketplace em tempo real.</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-full">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                days === p.value
                  ? 'bg-white shadow text-slate-900'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick receita strip */}
      <RevenueQuickStrip />

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : !data ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl">
          <p className="text-slate-500">Não foi possível carregar os dados.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Faturamento"
              value={fmtBRL(data.kpis.revenue)}
              growth={data.kpis.revenue_growth_pct}
              icon={DollarSign}
              gradient="from-blue-500 to-blue-700"
            />
            <KpiCard
              label="Pedidos"
              value={data.kpis.orders}
              growth={data.kpis.orders_growth_pct}
              icon={ShoppingBag}
              gradient="from-violet-500 to-violet-700"
            />
            <KpiCard
              label="Ticket Médio"
              value={fmtBRL(data.kpis.avg_ticket)}
              growth={data.kpis.avg_ticket_growth_pct}
              icon={TrendingUp}
              gradient="from-emerald-500 to-emerald-700"
            />
            <KpiCard
              label="Clientes Únicos"
              value={data.kpis.unique_customers}
              icon={Users}
              gradient="from-amber-500 to-amber-700"
            />
          </div>

          {/* Small stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat icon={StoreIcon} label="Lojas ativas" value={data.kpis.active_stores} sub={`de ${data.kpis.total_stores}`} />
            <MiniStat icon={Package} label="Produtos" value={data.kpis.total_products} />
            <MiniStat icon={ShoppingBag} label="Pgto confirmado" value={data.payment_dist?.confirmed || 0} sub="no período" />
            <MiniStat icon={TrendingUp} label="Aguardando" value={data.payment_dist?.pending || 0} sub="pgtos" />
          </div>

          {/* Revenue timeseries */}
          <Card className="border-slate-200 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Evolução do Faturamento
              </CardTitle>
              <p className="text-xs text-slate-500">Últimos {data.period_days} dias</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={timeseries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenue-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="dayLabel" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={fmtShort} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 6px 24px rgba(0,0,0,0.08)' }}
                    formatter={(v, key) => (key === 'revenue' ? [fmtBRL(v), 'Faturamento'] : [v, 'Pedidos'])}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    fill="url(#revenue-fill)"
                    animationDuration={800}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Two-column: top stores + categories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <StoreIcon className="w-5 h-5 text-violet-600" />
                  Top 5 Lojas
                </CardTitle>
                <p className="text-xs text-slate-500">Por faturamento no período</p>
              </CardHeader>
              <CardContent>
                {data.top_stores?.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.top_stores} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} tickFormatter={fmtShort} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="store_name" stroke="#94a3b8" fontSize={11} width={110} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                        formatter={(v) => [fmtBRL(v), 'Faturamento']}
                      />
                      <Bar dataKey="revenue" radius={[0, 8, 8, 0]} animationDuration={800}>
                        {data.top_stores.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState msg="Sem vendas no período" />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-emerald-600" />
                  Vendas por Categoria
                </CardTitle>
                <p className="text-xs text-slate-500">Distribuição no período</p>
              </CardHeader>
              <CardContent>
                {data.categories_breakdown?.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.categories_breakdown}
                        dataKey="revenue"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        animationDuration={800}
                      >
                        {data.categories_breakdown.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtBRL(v)} />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState msg="Sem categorias vendidas no período" />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Peak hours */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle>Horários de Maior Movimento</CardTitle>
              <p className="text-xs text-slate-500">Pedidos por hora do dia</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.peak_hours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="hour"
                    stroke="#94a3b8"
                    fontSize={11}
                    tickFormatter={(h) => `${h}h`}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                    formatter={(v) => [v, 'Pedidos']}
                    labelFormatter={(h) => `${h}h - ${h + 1}h`}
                  />
                  <Bar dataKey="orders" fill="#06b6d4" radius={[6, 6, 0, 0]} animationDuration={800} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, growth, icon: Icon, gradient }) => {
  const growthIsPos = growth != null && growth > 0;
  const growthIsNeg = growth != null && growth < 0;
  const GrowthIcon = growthIsPos ? TrendingUp : growthIsNeg ? TrendingDown : Minus;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-lg transition-shadow">
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {growth != null && (
        <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold">
          <GrowthIcon
            className={`w-3.5 h-3.5 ${
              growthIsPos ? 'text-emerald-600' : growthIsNeg ? 'text-rose-600' : 'text-slate-400'
            }`}
          />
          <span className={growthIsPos ? 'text-emerald-600' : growthIsNeg ? 'text-rose-600' : 'text-slate-500'}>
            {growth > 0 ? '+' : ''}{growth}%
          </span>
          <span className="text-slate-400 font-normal">vs período anterior</span>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500">
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="text-lg font-bold text-slate-900 leading-tight">
        {value} {sub && <span className="text-xs font-normal text-slate-400">{sub}</span>}
      </p>
    </div>
  </div>
);

const EmptyState = ({ msg }) => (
  <div className="text-center py-8 text-slate-400 text-sm">{msg}</div>
);

const RevenueQuickStrip = () => {
  const [rev, setRev] = useState({ hoje: null, mes: null, ano: null });
  useEffect(() => {
    Promise.all([
      dashboardAPI.premium(1),
      dashboardAPI.premium(30),
      dashboardAPI.premium(365),
    ]).then(([d1, d30, d365]) => {
      setRev({
        hoje: { revenue: d1.data.kpis.revenue, orders: d1.data.kpis.orders },
        mes: { revenue: d30.data.kpis.revenue, orders: d30.data.kpis.orders },
        ano: { revenue: d365.data.kpis.revenue, orders: d365.data.kpis.orders },
      });
    }).catch(() => {});
  }, []);
  const strips = [
    { label: 'Receita HOJE', data: rev.hoje, gradient: 'from-amber-400 to-orange-500', accent: 'text-amber-900' },
    { label: 'Receita do MÊS', data: rev.mes, gradient: 'from-blue-500 to-blue-700', accent: 'text-blue-900' },
    { label: 'Receita do ANO', data: rev.ano, gradient: 'from-violet-500 to-violet-700', accent: 'text-violet-900' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="revenue-strip">
      {strips.map((s) => (
        <div
          key={s.label}
          className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.gradient} p-5 text-white shadow-lg`}
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
          <p className="text-[11px] uppercase tracking-widest font-semibold text-white/80">{s.label}</p>
          {s.data ? (
            <>
              <p className="text-3xl font-bold mt-2">
                {s.data.revenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
              <p className="text-xs text-white/80 mt-1">{s.data.orders} pedido{s.data.orders !== 1 ? 's' : ''}</p>
            </>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-white/70 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Dashboard;
